import type { ComponentType } from "react";
import {
  Badge,
  BadgeProps,
  HStack,
  Icon,
  Text,
  TextProps,
  Tooltip,
} from "@chakra-ui/react";
import {
  FiArchive,
  FiCheckCircle,
  FiEdit3,
  FiLock,
  FiPause,
  FiTrash2,
} from "react-icons/fi";
import {
  MarketingAdSet,
  MarketingCampaign,
} from "common/interfaces/KYDMarketing";

type StatusBadgeStatus = MarketingCampaign["status"] | MarketingAdSet["status"];

export const colorForStatus = (status: StatusBadgeStatus) => {
  if (status === "ACTIVE") return "blue.100";
  if (status === "PAUSED") return "yellow.100";
  if (status === "ARCHIVED") return "gray.400";
  if (status === "TO_DELETE") return "gray.100";
  if (status === "DELETED") return "gray.100";
  if (status === "FROZEN") return "gray.100";
  if (status === "DRAFT") return "gray.100";
  return "gray.400";
};

export const getTextForStatus = (status: StatusBadgeStatus | string) => {
  if (!status) return "";
  if (status === "FROZEN")
    return "KYD freezes these objects when they were changed from the ads manager side and use features not supported by KYD ads manager";
  return status.toLowerCase();
};

const badgeStyles: BadgeProps = {
  borderRadius: "0.625rem",
  bg: "#AEDFAA",
  display: "flex",
  px: 2,
  justifyContent: "center",
  height: 4,
  alignItems: "center",
  gap: "0.625rem",
  alignSelf: "stretch",
  cursor: "text",
  userSelect: "text" as const,
  _hover: { opacity: 0.8 },
};

const textStyles: TextProps = {
  color: "#1F531A",
  fontFamily: "Work Sans",
  fontSize: "xx-small",
  fontWeight: 500,
  lineHeight: "100%",
};

const colorSchemeForStatus = (status: StatusBadgeStatus) => {
  if (status === "ACTIVE") return "green.100";
  if (status === "PAUSED") return "yellow.100";
  if (status === "ARCHIVED") return "gray.400";
  if (status === "TO_DELETE") return "gray.100";
  if (status === "DELETED") return "gray.100";
  if (status === "DRAFT") return "gray.100";
  if (status === "FROZEN") return "gray.100";
  return "gray.100";
};

export const StatusBadge = ({ status }: { status: StatusBadgeStatus }) => {
  /** Active is the default "healthy" state — no badge (same as performance "On Track"). */
  if (status === "ACTIVE") return null;

  const statusText = getTextForStatus(status);

  const handleTextSelect = (e: React.MouseEvent) => {
    e.preventDefault();
    const selection = window.getSelection();
    if (selection) {
      const range = document.createRange();
      range.selectNodeContents(e.currentTarget);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  };

  const label = status?.toUpperCase() ?? "";

  const badge = (
    <Badge
      {...badgeStyles}
      colorScheme={colorSchemeForStatus(status)}
      bg={colorForStatus(status)}
    >
      <HStack gap="0.625rem">
        <Text
          {...textStyles}
          textTransform="capitalize"
          onDoubleClick={handleTextSelect}
        >
          {label}
        </Text>
      </HStack>
    </Badge>
  );

  return (
    <Tooltip
      label={`${status}:\n${statusText}`}
      hasArrow
      placement="top"
      bg="gray.700"
      color="white"
      fontSize="sm"
    >
      {badge}
    </Tooltip>
  );
};

const formatStatusTitle = (status: string) =>
  status
    .split("_")
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ");

const isKnownBadgeStatus = (s: string): s is StatusBadgeStatus =>
  [
    "ACTIVE",
    "PAUSED",
    "DRAFT",
    "ARCHIVED",
    "TO_DELETE",
    "DELETED",
    "FROZEN",
    "COMPLETED",
  ].includes(s);

const asBadgeStatus = (status: string): StatusBadgeStatus =>
  status as StatusBadgeStatus;

/**
 * Label + hint for campaign / ad set / ad detail: how KYD status relates to Meta (published vs draft vs paused).
 */
export function getMetaSyncStatusPresentation(
  status: string | undefined,
  hasExternalId: boolean
): { label: string; hint: string; badgeStatus: StatusBadgeStatus } {
  const raw = (status ?? "DRAFT").toUpperCase();
  const s = (isKnownBadgeStatus(raw) ? raw : "DRAFT") as StatusBadgeStatus;

  if (!hasExternalId) {
    if (s === "DRAFT" || s === "PAUSED") {
      return {
        label: "Draft",
        hint: "Not published to Meta.",
        badgeStatus: asBadgeStatus("DRAFT"),
      };
    }
    if (s === "FROZEN") {
      return {
        label: "Frozen",
        hint: getTextForStatus("FROZEN"),
        badgeStatus: asBadgeStatus("FROZEN"),
      };
    }
    if (s === "ARCHIVED" || s === "DELETED" || s === "TO_DELETE") {
      return {
        label: formatStatusTitle(s),
        hint: "Not published to Meta.",
        badgeStatus: s,
      };
    }
    if (s === "COMPLETED") {
      return {
        label: "Completed",
        hint: "Not published to Meta.",
        badgeStatus: asBadgeStatus("COMPLETED"),
      };
    }
    return {
      label: "Draft",
      hint: "Not published to Meta.",
      badgeStatus: asBadgeStatus("DRAFT"),
    };
  }

  if (s === "ACTIVE") {
    return {
      label: "Active",
      hint: "Published to Meta.",
      badgeStatus: asBadgeStatus("ACTIVE"),
    };
  }
  if (s === "PAUSED") {
    return {
      label: "Paused",
      hint: "Synced to Meta but paused.",
      badgeStatus: asBadgeStatus("PAUSED"),
    };
  }
  if (s === "DRAFT") {
    return {
      label: "Draft",
      hint: "On Meta as a draft.",
      badgeStatus: asBadgeStatus("DRAFT"),
    };
  }
  if (s === "COMPLETED") {
    return {
      label: "Completed",
      hint: "Campaign completed on Meta.",
      badgeStatus: asBadgeStatus("COMPLETED"),
    };
  }
  if (s === "ARCHIVED") {
    return {
      label: "Archived",
      hint: "Archived on Meta.",
      badgeStatus: asBadgeStatus("ARCHIVED"),
    };
  }
  if (s === "FROZEN") {
    return {
      label: "Frozen",
      hint: getTextForStatus("FROZEN"),
      badgeStatus: asBadgeStatus("FROZEN"),
    };
  }
  if (s === "DELETED" || s === "TO_DELETE") {
    return {
      label: formatStatusTitle(s),
      hint: "Open Ads Manager for the latest state on Meta.",
      badgeStatus: s,
    };
  }

  return {
    label: formatStatusTitle(raw),
    hint: "Open Ads Manager for the latest state on Meta.",
    badgeStatus: asBadgeStatus("DRAFT"),
  };
}

const handleMetaSyncLabelSelect = (e: React.MouseEvent) => {
  e.preventDefault();
  const selection = window.getSelection();
  if (selection) {
    const range = document.createRange();
    range.selectNodeContents(e.currentTarget);
    selection.removeAllRanges();
    selection.addRange(range);
  }
};

/** Figma KYD Dashboard status (node 197:8976): pill, icon + label, neutral slate palette. */
const engageStatusBadgeStyles: BadgeProps = {
  borderRadius: "10px",
  bg: "#d2d7df",
  display: "inline-flex",
  px: 2,
  py: "3px",
  h: "auto",
  minH: 0,
  alignItems: "center",
  flexShrink: 0,
  cursor: "default",
  userSelect: "text" as const,
  _hover: { opacity: 0.92 },
};

const engageStatusTextStyles: TextProps = {
  color: "#4a5568",
  fontFamily: "Work Sans",
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: 1,
};

function engageStatusIconFor(
  badgeStatus: StatusBadgeStatus
): ComponentType<{ size?: string }> {
  switch (badgeStatus) {
    case "PAUSED":
      return FiPause;
    case "DRAFT":
      return FiEdit3;
    case "ARCHIVED":
      return FiArchive;
    case "FROZEN":
      return FiLock;
    case "DELETED":
    case "TO_DELETE":
      return FiTrash2;
    case "COMPLETED":
      return FiCheckCircle;
    default:
      return FiEdit3;
  }
}

/** Engage detail status pill when not active; active (published, live) renders nothing like list `StatusBadge`. Tooltip on hover explains Meta sync. */
export function MetaSyncStatusLabel({
  status,
  hasExternalId,
}: {
  status: string | undefined;
  hasExternalId: boolean;
}) {
  const { label, hint, badgeStatus } = getMetaSyncStatusPresentation(
    status,
    hasExternalId
  );

  /** Same as list `StatusBadge`: active is the default healthy state — no badge. */
  if (badgeStatus === "ACTIVE") {
    return null;
  }

  const StatusIcon = engageStatusIconFor(badgeStatus);

  const badge = (
    <Badge {...engageStatusBadgeStyles}>
      <HStack gap={1} alignItems="center">
        <Icon
          as={StatusIcon}
          aria-hidden
          boxSize="10px"
          color="#4a5568"
          flexShrink={0}
        />
        <Text
          {...engageStatusTextStyles}
          textTransform="capitalize"
          onDoubleClick={handleMetaSyncLabelSelect}
        >
          {label}
        </Text>
      </HStack>
    </Badge>
  );

  return (
    <Tooltip
      label={hint}
      hasArrow
      placement="top"
      bg="gray.700"
      color="white"
      fontSize="sm"
      maxW="280px"
      whiteSpace="normal"
    >
      {badge}
    </Tooltip>
  );
}
